import { computed, effect, inject, Injectable } from '@angular/core';
import { ComponentStoreV2Factory } from '../../../../shared/store/common/component-store-v2.factory';
import { globalStore } from '../../../../shared/shared.global-service';
import {
    selectCurrentService,
    selectFolder,
    selectServices,
    selectServicesPublic,
} from '../../../store/person/person.selectors';
import { isFolderReadOnly } from '../../../../../../../../types/src/services/enum/folder-status.enum';
import { DashboardSettingsObject } from '@libs/types/src/services/object-type/dashboard-settings.object';
import {
    FOLDER_DASHBOARD_BASIC_INFO_PROPERTIES_KEY,
    FOLDER_DASHBOARD_WIDGET_SETTINGS_KEY,
} from '@libs/types/src/services/types/folder-dashboard-settings.type';
import { FilterBuilder } from '../../../helper/filter.builder';
import { ServiceDataShareObject } from '@libs/types/src/services/object-type/service/service-data-share.object';
import { FolderDashboardWidgetsEnum } from '@libs/types/src/services/enum/folder-dashboard-widgets.enum';
import {
    COMMON_SERVICE_SETTINGS_DEFAULT,
    CommonServiceSettingsType,
    SERVICE_SETTINGS_DASHBOARD_ANAMNESIS_FIELDS_DEFAULT,
    SERVICE_SETTINGS_DASHBOARD_TYPE_KEY,
    ServiceSettingsDashboardAnamnesisFieldsType,
    ServiceSettingsDashboardType,
} from '@libs/types/src/services/types/common-service-settings.type';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { SessionService } from '../../../../../services/session.service';
import { PersonGridItemObject } from '@it2go/types';
import { FolderPersonObject } from '@libs/types/src/services/object-type/folder/folder-person.object';
import {
    SERVICE_SETTINGS_FOLDER_PERSONS_DEFAULT,
    SERVICE_SETTINGS_FOLDER_PERSONS_KEY,
    ServiceSettingsFolderPersonsType,
} from '@libs/types/src/services/types/service-settings-folder-persons.type';
import { folderPersonRowFrag } from '../../../../../services/types/folder.types';
import { personFrag } from '../../../service/types/person.types';
import { featureOn } from '../../../../../feature-flag';
import { routeParams } from '../../../../shared/shared.global-service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import {
    folderPersonsLabel,
    getSharingFolderIds,
    isFolderPersonActive,
} from '../../../../../utils/folder.utils';
import { SERVICE_SETTINGS_ANAMNESIS_TYPE_KEY } from '@libs/types/src/services/types/service-settings-anamnesis.type';

export interface FolderSharingObject extends ServiceDataShareObject {
    folderId: number;
}

@Injectable()
export class FolderDashboardPageStore extends ComponentStoreV2Factory {

    public readonly session = inject(SessionService);
    private readonly breadcrumbService = inject(BreadcrumbService);

    constructor() {
        super();
        this.api.getUserProfile(
            `
               employments {
                     employmentTypeName
                }
            `,
        ).then();

        this.onSuccess(
            () => this.loadFolderPersons(),
            'createFolderPerson',
            'createFolderPersonWithNewPerson',
            'setMainFolderPerson',
            'removeFolderPerson',
            'createFolderPersonHistory',
            'updateFolderPersonHistory',
            'deleteFolderPersonHistory',
        );
    }

    currentUserId = this.globalGlobalStore.loggedUserStore.currentUserId();

    currentUserEmploymentNames = computed(() => {
        return this.data.getUserProfile()?.employments.map((i: any) => i.employmentTypeName) || [];
    });

    currentService = globalStore!.selectSignal(selectCurrentService);
    services = globalStore!.selectSignal(selectServices);

    folder = globalStore!.selectSignal(selectFolder);
    person = computed(() => this.folder()?.person ?? null);

    folderRelatedServices = globalStore!.selectSignal(selectServicesPublic);

    private readonly folderRecordReadAllowed = this.isAllowed('folderRecord.read');

    public readonly anamnesisSharingIds = computed<number[]>(() =>
        getSharingFolderIds(
            this.currentService()?.id,
            this.services(),
            this.folder()?.relatedFolders,
            SERVICE_SETTINGS_ANAMNESIS_TYPE_KEY,
        ) ?? [],
    );

    public readonly anamnesisFolderIds = computed<number[]>(() => {
        const folderId = this.folder()?.id;
        if (!folderId) return [];

        return [...new Set([folderId, ...this.anamnesisSharingIds()])]
            .sort((first, second) => first - second);
    }, {
        equal: (previous, current) => previous.length === current.length
            && previous.every((folderId, index) => folderId === current[index]),
    });

    public readonly canViewAnamnesis = computed(() =>
        this.folderRecordReadAllowed() || this.anamnesisSharingIds().length > 0,
    );

    isTerminated = computed(() => isFolderReadOnly(this.folder()));

    widgetSettings = computed(() => {
        const item = this.data.getDashboardSettings()?.items
            .find((i: DashboardSettingsObject) => i.key === FOLDER_DASHBOARD_WIDGET_SETTINGS_KEY && i.serviceId == this.currentService()?.id);
        return item ? JSON.parse(item.data) : undefined;
    });

    basicInfoProperties = computed(() => {
        // nastavení je uložené per služba - bez kontroly serviceId se do karty dostane nastavení
        // předchozí vybrané služby, které o novějších vlastnostech karty nemusí vědět
        const item = this.data.getDashboardSettings()?.items
            .find((i: DashboardSettingsObject) => i.key === FOLDER_DASHBOARD_BASIC_INFO_PROPERTIES_KEY
                && i.serviceId == this.currentService()?.id);
        return item ? JSON.parse(item.data) : undefined;
    });

    // -------- sluzby, se kterymi se muze sdilet folder --------
    allowedServicesToShareFolder = computed<ServiceDataShareObject[]>(() => {
        return this.data.getServiceDataShares()?.items
            .filter((i: ServiceDataShareObject) =>
                this.folderRelatedServices().some((rel) => rel.id === i.serviceToShareId) && i.onlyMarkedFolders)
            .map((i: ServiceDataShareObject) => i.serviceToShare) || [];
    });

    getServiceDataSharesEffect = effect(async () => {
        const serviceId = this.currentService()?.id;
        if (!serviceId) return;

        await this.api.getServiceDataShares(FilterBuilder.singlePage({
            serviceId,
        }));
    });

    // -------- sluzby (pripadne jen slozky), ktere sdileji s aktualni slozkou --------
    sharingServices = computed<ServiceDataShareObject[]>(() => {
        return this.data.getSharingServices()?.items
            .filter((i: ServiceDataShareObject) => !i.employmentNames.length || i.employmentNames
                .some((name: string) => this.currentUserEmploymentNames().includes(name)))
            .filter((ss: ServiceDataShareObject) => this.folderRelatedServices()
                .some(i => i.id === ss.serviceId)) || [];
    });

    sharingServicesToAllFolders = computed<ServiceDataShareObject[]>(() => {
        return this.sharingServices().filter((i: ServiceDataShareObject) => !i.onlyMarkedFolders);
    });

    sharingServicesToMarkedFolders = computed<ServiceDataShareObject[]>(() => {
        return this.sharingServices().filter((i: ServiceDataShareObject) => i.onlyMarkedFolders);
    });

    getSharingServicesEffect = effect(async () => {
        const serviceId = this.currentService()?.id;
        if (!serviceId) return;

        await this.api.getSharingServices(FilterBuilder.singlePage({
            serviceToShareId: serviceId,
        }));
    });

    getMarkedFoldersEffect = effect(async () => {
        const serviceId = this.currentService()?.id;
        if (this.sharingServicesToMarkedFolders().length === 0 || !serviceId) return;

        const filter = new FilterBuilder()
            .where('dateFrom', new Date().toISOString(), 'LTE')
            .where('dateTo', new Date().toISOString(), 'GTE')
            .orWhere('dateTo', true, 'EMPTY')
            .serviceId(serviceId);

        await this.api.getFolderDataShares(filter.create());
    });

    public settingsData = computed<ServiceSettingsDashboardType>(() => {
        const service = this.currentService();
        const setting = service?.serviceSettings?.find((it) => it.key === SERVICE_SETTINGS_DASHBOARD_TYPE_KEY);

        return JSON.parse(setting?.data || '{}') as ServiceSettingsDashboardType;
    });

    public readonly anamnesisFields = computed<ServiceSettingsDashboardAnamnesisFieldsType>(() => ({
        ...SERVICE_SETTINGS_DASHBOARD_ANAMNESIS_FIELDS_DEFAULT,
        ...(this.settingsData().anamnesisFields ?? {}),
    }));

    public isHealthCare = computed(() => this.currentService()?.type === ServiceTypeEnum.HEALTH_CARE);

    public isSocial = computed(() => this.currentService()?.type === ServiceTypeEnum.SOCIAL);

    // -------- osoby ve složce (folder_person) --------
    public folderPersonsSettings = computed<ServiceSettingsFolderPersonsType>(() => this.getServiceSettings(
        SERVICE_SETTINGS_FOLDER_PERSONS_KEY,
        SERVICE_SETTINGS_FOLDER_PERSONS_DEFAULT,
    ) as ServiceSettingsFolderPersonsType);

    public multiPersonEnabled = computed(() =>
        featureOn.FOLDER_MULTI_PERSON && this.isSocial() && !!this.folderPersonsSettings().enabled,
    );

    /**
     * Osoby aktuálně ve složce. Odebraná osoba tu není - její pobyty zůstávají jen v historii
     * (dialog historie pobytů). Zemřelá primární osoba ve složce zůstává a na nástěnce se jen
     * graficky odliší, zemřelou neprimární osobu backend ze složky odebere.
     */
    public folderPersons = computed<FolderPersonObject[]>(() => this.data.getFolderPersons()?.items ?? []);

    /** Živé osoby ve složce - popisek složky nemá zmiňovat zemřelé. */
    public presentFolderPersons = computed<FolderPersonObject[]>(() =>
        this.folderPersons().filter((it) => !it.isDeceased),
    );

    /**
     * Vlastník složky (folders.person_id) - to je osoba na hlavní kartě `personInfo`.
     * Není to "ta primární osoba", primárních (isMain) může být ve složce víc; hvězdičku na
     * kartě řídí až samo `isMain` v app-person-basic-info.
     */
    public ownerFolderPerson = computed<FolderPersonObject | null>(() => {
        const ownerId = this.person()?.id;

        return this.folderPersons().find((it) => it.personId === ownerId) ?? null;
    });

    /**
     * Všechny ostatní osoby složky - primární i ne, vlastník je už na hlavní kartě. Osoby, které
     * na službě už jsou, se řadí před ty s pobytem začínajícím teprve v budoucnu.
     */
    private secondaryFolderPersons = computed<FolderPersonObject[]>(() => {
        const ownerId = this.person()?.id;

        return this.folderPersons()
            .filter((it) => it.personId !== ownerId)
            .sort((a, b) => {
                const byActivity = Number(isFolderPersonActive(b)) - Number(isFolderPersonActive(a));
                if (byActivity) return byActivity;

                return new Date(b.dateFrom ?? 0).getTime() - new Date(a.dateFrom ?? 0).getTime();
            });
    });

    /**
     * Karta potřebuje celou osobu (socialData, keyWorker, currentAddress, ...) - grid vrací
     * jen zkrácený json blob, takže se osoby dotahují zvlášť.
     */
    public secondaryPersonCards = computed<{ folderPerson: FolderPersonObject, person: PersonGridItemObject }[]>(() => {
        const persons: PersonGridItemObject[] = this.data.getPersons()?.items ?? [];

        return this.secondaryFolderPersons()
            .map((folderPerson) => ({
                folderPerson,
                person: persons.find((it) => it.id === folderPerson.personId)!,
            }))
            .filter((it) => !!it.person);
    });

    /**
     * Drobeček složky nastavuje FolderDetailPageStore z načtené složky - po změně osob (přidání,
     * ukončení, změna primární) se složka nepřenačítá, takže ho přepíšeme z čerstvého gridu.
     */
    updateBreadcrumbEffect = effect(() => {
        const folderPersons = this.presentFolderPersons();
        if (!this.multiPersonEnabled() || !folderPersons.length) return;

        this.breadcrumbService.set(
            `@${BreadcrumbsEnum.Folder}`,
            `${BreadcrumbsEnum.Folder}|${folderPersonsLabel(this.person(), folderPersons)}`,
        );
    });

    getFolderPersonsEffect = effect(async () => {
        if (!this.multiPersonEnabled() || !routeParams.folderId()) return;

        await this.loadFolderPersons();
    });

    getSecondaryPersonsEffect = effect(async () => {
        const ids = this.secondaryFolderPersons().map((it) => it.personId);
        if (!ids.length) return;

        await this.api.getPersons(
            new FilterBuilder().where('id', ids, 'IN').limit(999).create(),
            personFrag,
        );
    });

    public async loadFolderPersons(): Promise<void> {
        const folderId = routeParams.folderId();
        if (!folderId || !this.multiPersonEnabled()) return;

        await this.api.getFolderPersons(
            // bez filtru na úmrtí - zemřelá primární osoba ve složce zůstává (se zašedlou kartou),
            // u ostatních kartu drží do chvíle, než je reconcile ze složky odebere
            new FilterBuilder()
                .folderId(folderId)
                .limit(999)
                .create(),
            folderPersonRowFrag,
        );
    }

    public getServiceSettings(key: string, defaultValue: any = COMMON_SERVICE_SETTINGS_DEFAULT): CommonServiceSettingsType {
        const settings = this.currentService()?.serviceSettings || [];
        return JSON.parse(settings.find((it) =>
            it.key === key)?.data || JSON.stringify(defaultValue));
    }
}

export function getShareFolderIds(sharingFolders: FolderSharingObject[], widget: FolderDashboardWidgetsEnum): string[] {
    return sharingFolders
        .filter((f) => !f?.widgets?.length || f?.widgets?.includes(widget))
        .map((f) => f.folderId.toString());
}
